import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { uniqBy } from 'lodash';

import { useNotification } from 'bundles/skills-common/private/admin-skill-tracking/Notifications';
import { FILTER_ALL } from 'bundles/skills-common/private/admin-skill-tracking/TrendingSkillsFilters';
import type {
  FetchTrendingSkillsQuery,
  FetchTrendingSkillsQueryVariables,
} from 'bundles/skills-common/private/admin-skill-tracking/data/__generated__/fetchTrendingSkills';
import type {
  FetchTrendingSkillsFromProgramQuery,
  FetchTrendingSkillsFromProgramQueryVariables,
} from 'bundles/skills-common/private/admin-skill-tracking/data/__generated__/fetchTrendingSkillsFromProgram';
import fetchTrendingSkills from 'bundles/skills-common/private/admin-skill-tracking/data/fetchTrendingSkills.graphql';
import fetchTrendingSkillsFromProgram from 'bundles/skills-common/private/admin-skill-tracking/data/fetchTrendingSkillsFromProgram.graphql';
import SKILL_DATA_QUERY from 'bundles/skills-common/private/admin-skill-tracking/data/skillDataQuery';
import type {
  SkillDataQueryData,
  SkillDataQueryVariables,
} from 'bundles/skills-common/private/admin-skill-tracking/data/skillDataQuery';

import _t from 'i18n!nls/skills-common';

export type SkillWithTrends = {
  id: string;
  name: string;
  learners: number;
  change: number;
  domains: string[];
  percentageOfAllLearners: number;
  rank: number;
};

const getMetricsTimeFrame = (timeFrame: number) => {
  if (timeFrame === 30) return 'THIRTY_DAYS';
  if (timeFrame === 60) return 'SIXTY_DAYS';
  return 'NINETY_DAYS';
};

const useFetchData = (orgId: string, programId: string, timeFrame: number) => {
  const resultsFromOrg = useQuery<FetchTrendingSkillsQuery, FetchTrendingSkillsQueryVariables>(fetchTrendingSkills, {
    variables: {
      orgId,
      metricsTimeFrame: getMetricsTimeFrame(timeFrame),
    },
    skip: !!programId,
    context: {
      clientName: 'gatewayGql',
    },
  });
  const resultsFromProgram = useQuery<
    FetchTrendingSkillsFromProgramQuery,
    FetchTrendingSkillsFromProgramQueryVariables
  >(fetchTrendingSkillsFromProgram, {
    variables: {
      programId,
      metricsTimeFrame: getMetricsTimeFrame(timeFrame),
    },
    skip: !programId,
    context: {
      clientName: 'gatewayGql',
    },
  });

  if (!programId) {
    return {
      ...resultsFromOrg,
      trendingSkillsInsights:
        resultsFromOrg.data?.EnterpriseSkillsInsights?.querySkillsInsightsByOrg?.trendingSkillsInsights ?? [],
    };
  }
  return {
    ...resultsFromProgram,
    trendingSkillsInsights:
      resultsFromProgram.data?.EnterpriseSkillsInsights?.querySkillsInsightsByProgram?.trendingSkillsInsights ?? [],
  };
};

const useFetchTrendingSkills = (
  orgId: string,
  programId: string,
  timeFrame: number,
  search = '',
  skillDomains = FILTER_ALL
) => {
  const { trendingSkillsInsights, loading: loadingTrendingSkills, error } = useFetchData(orgId, programId, timeFrame);
  const createNotification = useNotification();
  const { data: skillDomainData, loading: loadingSkillData } = useQuery<SkillDataQueryData, SkillDataQueryVariables>(
    SKILL_DATA_QUERY,
    {
      skip: loadingTrendingSkills,
      variables: {
        input: {
          skillIds: trendingSkillsInsights.map((skill) => skill.skillId),
        },
      },
    }
  );

  const skills: SkillWithTrends[] = trendingSkillsInsights.map((skill) => ({
    id: skill.skillId,
    name: skill.skillName,
    learners: skill.numOfLearners,
    change: skill.changePercentage,
    percentageOfAllLearners: skill.percentageOfAllLearnersAssessed,
    rank: skill.rank,
    domains: skillDomainData?.MultiGetSkills.skills.find((s) => s.skillId === skill.skillId)?.domainIds ?? [],
  }));

  const filteredSkills = skills.filter((skill) => {
    let filteredOut = false;

    if (skillDomains !== FILTER_ALL) {
      filteredOut ||= !skill.domains.includes(skillDomains);
    }

    filteredOut ||= !skill.name.toLowerCase().includes(search.toLowerCase());

    return !filteredOut;
  });

  const loading = loadingTrendingSkills || loadingSkillData;

  useEffect(() => {
    if (error) {
      createNotification({
        message: _t(
          'There was an error fetching the data. Please try again later, or contact Coursera if the error persists.'
        ),
        type: 'error',
      });
    }
  }, [createNotification, error]);

  return {
    loading,
    skills: uniqBy(filteredSkills, (skill) => skill.id).sort((skillA, skillB) => skillA.rank - skillB.rank),
  };
};

export { getMetricsTimeFrame };
export default useFetchTrendingSkills;
